/**
 * useDublettenMerge — Datenzugriff für den Dubletten-Merge-Wizard (#383).
 *
 * Liest die Admin-Wrapper-View `crm_kontakt_dubletten_admin` (#376) seitenweise
 * (PAGE_SIZE=50) und lädt die zugehörigen Kontaktdetails. Mutationen rufen die
 * Backend-RPCs `crm_kontakt_merge` und `crm_kontakt_merge_undo` (#377) auf.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { CRMKontakt } from '@/hooks/useCRM';

export type DublettenMatchReason = 'email' | 'phone' | 'fuzzy';

export interface DublettenPaar {
  kontakt_a_id: string;
  kontakt_b_id: string;
  match_reason: DublettenMatchReason;
  score: number;
  erkannt_am: string;
}

export const PAGE_SIZE = 50;

function dedupPaare(list: DublettenPaar[]): DublettenPaar[] {
  return [
    ...new Map(
      list.map((p) => [`${p.kontakt_a_id}|${p.kontakt_b_id}|${p.match_reason}`, p]),
    ).values(),
  ];
}

export function useDublettenMerge() {
  const [paare, setPaare] = useState<DublettenPaar[]>([]);
  const [kontakteById, setKontakteById] = useState<Record<string, CRMKontakt>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const loadKontakte = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return {} as Record<string, CRMKontakt>;
    const { data: kontakte, error: kontaktErr } = await supabase
      .from('crm_kontakte')
      .select('*')
      .in('id', ids);
    if (kontaktErr) throw kontaktErr;
    const map: Record<string, CRMKontakt> = {};
    (kontakte ?? []).forEach((k) => {
      map[k.id] = k as CRMKontakt;
    });
    return map;
  }, []);

  const loadPage = useCallback(async (page: number): Promise<DublettenPaar[]> => {
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const { data, error: paarErr } = await supabase
      .from('crm_kontakt_dubletten_admin')
      .select('*')
      .order('score', { ascending: false })
      .range(from, to);
    if (paarErr) throw paarErr;
    return (data ?? []) as DublettenPaar[];
  }, []);

  const loadDubletten = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await loadPage(0);
      setPaare(dedupPaare(list));
      setHasMore(list.length === PAGE_SIZE);
      const ids = Array.from(
        new Set(list.flatMap((p) => [p.kontakt_a_id, p.kontakt_b_id])),
      );
      setKontakteById(await loadKontakte(ids));
    } catch (e: any) {
      console.error('crm_kontakt_dubletten fetch error:', e);
      setError(e?.message ?? String(e));
      setPaare([]);
      setKontakteById({});
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loadPage, loadKontakte]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = Math.floor(paare.length / PAGE_SIZE);
      const list = await loadPage(nextPage);
      setHasMore(list.length === PAGE_SIZE);
      const newIds = Array.from(
        new Set(list.flatMap((p) => [p.kontakt_a_id, p.kontakt_b_id])),
      ).filter((id) => !kontakteById[id]);
      const additions = await loadKontakte(newIds);
      setPaare((prev) => dedupPaare([...prev, ...list]));
      setKontakteById((prev) => ({ ...prev, ...additions }));
    } catch (e: any) {
      console.error('crm_kontakt_dubletten loadMore error:', e);
      setError(e?.message ?? String(e));
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, paare.length, kontakteById, loadPage, loadKontakte]);

  useEffect(() => {
    loadDubletten();
  }, [loadDubletten]);

  const merge = useCallback(
    async (
      masterId: string,
      duplicateId: string,
      fieldOverrides: Record<string, string>,
    ): Promise<string> => {
      const { data, error: err } = await supabase.rpc('crm_kontakt_merge', {
        master_id: masterId,
        duplicate_id: duplicateId,
        field_overrides: fieldOverrides,
      });
      if (err) throw err;
      setPaare((prev) =>
        prev.filter(
          (p) =>
            p.kontakt_a_id !== duplicateId &&
            p.kontakt_b_id !== duplicateId,
        ),
      );
      return data as string;
    },
    [],
  );

  const undoMerge = useCallback(async (duplicateId: string): Promise<string> => {
    const { data, error: err } = await supabase.rpc('crm_kontakt_merge_undo', {
      duplicate_id: duplicateId,
    });
    if (err) throw err;
    await loadDubletten();
    return data as string;
  }, [loadDubletten]);

  return {
    paare,
    kontakteById,
    loading,
    loadingMore,
    hasMore,
    error,
    fetch: loadDubletten,
    loadMore,
    merge,
    undoMerge,
  };
}
